/**
 * Tri, groupes et couleurs de la liste de liens de pièces du viewer /review
 * (retours François 2026-07-29) :
 *  1. pièces CONTRÔLÉES VIA LE FORMULAIRE, dans l'ordre logique du dossier ;
 *  2. pièces contrôlées AUTREMENT (cession, mandat, assurance, CT) — isolées
 *     en fin de liste, chacune avec SA couleur ;
 *  3. pièces NON RECONNUES (classifieur « unknown » / jamais analysées),
 *     libellées par leur NOM DE FICHIER (seule info fiable disponible).
 *
 * Helpers PURS (pattern piecesGridHebergeur) → testables en environnement node.
 * Le tri se fait par docType — plus robuste que l'ancien `docLinkRank` qui
 * sniffait les libellés.
 */

export type DocLinkGroup = 'formulaire' | 'hors_formulaire' | 'non_reconnue';

/**
 * Pièces dont le contrôle ne passe pas (ou plus) par le formulaire : isolées
 * en fin de liste, colorées par type. L'ordre du tableau est l'ordre d'affichage.
 */
export const HORS_FORMULAIRE_ORDER: readonly string[] = ['cerfa_cession', 'mandat', 'assurance', 'controle_technique'];

/**
 * Couleur par type de pièce « hors formulaire » (une couleur PAR TYPE, demande
 * François) : fond clair + bordure + texte foncé, depuis les tokens du design
 * system. Le jaune reste réservé à l'état actif du lien, le rouge aux erreurs.
 */
export const HORS_FORMULAIRE_COLOR_KEYS: Record<string, 'blue' | 'purple' | 'green' | 'orange'> = {
  cerfa_cession: 'blue',
  mandat: 'purple',
  assurance: 'green',
  controle_technique: 'orange',
};

/** Ordre logique du dossier pour le groupe « formulaire ». Inconnu → fin de groupe. */
const FORMULAIRE_ORDER: readonly string[] = [
  'carte_grise',
  'recepisse_da',
  'cerfa_demande_immat',
  'piece_identite',
  'piece_identite_verso',
  'piece_identite_cotitulaire',
  'piece_identite_cotitulaire_verso',
  'permis_conduire',
  'permis_conduire_verso',
  'permis_conduire_cotitulaire',
  'permis_conduire_cotitulaire_verso',
  'justif_domicile',
  'attestation_hebergement',
  'piece_identite_hebergeur',
  'piece_identite_hebergeur_verso',
  'kbis',
  'kbis_acheteur',
  'kbis_vendeur',
  'acte_deces',
  'justif_succession',
];

export const docLinkGroupOf = (docType: string | undefined): DocLinkGroup => {
  if (!docType || docType === 'unknown') return 'non_reconnue';
  if (HORS_FORMULAIRE_ORDER.includes(docType)) return 'hors_formulaire';
  return 'formulaire';
};

const GROUP_RANK: Record<DocLinkGroup, number> = { formulaire: 0, hors_formulaire: 1, non_reconnue: 2 };

const rankInGroup = (docType: string | undefined, group: DocLinkGroup): number => {
  const order = group === 'hors_formulaire' ? HORS_FORMULAIRE_ORDER : FORMULAIRE_ORDER;
  const idx = docType ? order.indexOf(docType) : -1;
  return idx === -1 ? order.length : idx;
};

export interface SortableDocLink {
  docType?: string;
  /** Index d'origine dans `files` — préservé pour la navigation du viewer. */
  idx: number;
  /** Confiance OCR (0-100) — départage les doublons d'un même type. */
  confidence?: number;
}

/**
 * Réduit les liens à UN par type de pièce (retour Luc 2026-07-30) : un scan
 * « livre » multi-pièces déposé en plusieurs exemplaires produit autant de
 * lignes « Mandat d'immatriculation », « Contrôle technique »… qu'il y a de
 * fichiers, ce qui noie la liste.
 *
 * Le représentant d'un type est :
 *  1. le fichier ACTUELLEMENT affiché s'il appartient à ce type — le lien
 *     surligné correspond ainsi toujours à ce que le relecteur voit ;
 *  2. sinon la meilleure confiance OCR, puis le plus petit index (stabilité).
 *
 * Les pièces NON RECONNUES ne sont jamais dédupliquées : chacune est un
 * fichier distinct que le relecteur doit pouvoir ouvrir individuellement.
 * Pur → testable directement.
 */
export const dedupeDocLinksByType = <T extends SortableDocLink>(links: readonly T[], currentIndex: number): T[] => {
  const bestByType = new Map<string, T>();
  const kept: T[] = [];

  links.forEach((link) => {
    const { docType } = link;
    if (!docType || docLinkGroupOf(docType) === 'non_reconnue') {
      kept.push(link);
      return;
    }
    const existing = bestByType.get(docType);
    if (!existing) {
      bestByType.set(docType, link);
      return;
    }
    if (existing.idx === currentIndex) return;
    const isActive = link.idx === currentIndex;
    const isBetter = (link.confidence ?? 0) > (existing.confidence ?? 0);
    if (isActive || isBetter) bestByType.set(docType, link);
  });

  return [...kept, ...bestByType.values()];
};

/**
 * Trie les liens : groupe (formulaire → hors formulaire → non reconnues), puis
 * ordre logique dans le groupe, puis index d'origine (stabilité). Le tri est
 * purement visuel : chaque lien garde son index d'origine pour la navigation.
 */
export const sortDocLinks = <T extends SortableDocLink>(links: readonly T[]): T[] =>
  [...links].sort((a, b) => {
    const groupA = docLinkGroupOf(a.docType);
    const groupB = docLinkGroupOf(b.docType);
    if (groupA !== groupB) return GROUP_RANK[groupA] - GROUP_RANK[groupB];
    const rankDiff = rankInGroup(a.docType, groupA) - rankInGroup(b.docType, groupB);
    return rankDiff !== 0 ? rankDiff : a.idx - b.idx;
  });

/**
 * Nom de fichier lisible depuis une URL de stockage (Firebase encode le chemin
 * dans le pathname : `.../o/cg%2FDC-39685-3.pdf?alt=media&…`). Fallback : URL
 * tronquée si le parsing échoue.
 */
export const fileNameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const decoded = decodeURIComponent(pathname);
    const lastSegment = decoded.split('/').pop() ?? decoded;
    return lastSegment || url.slice(0, 40);
  } catch {
    const withoutQuery = url.split('?')[0];
    const decoded = decodeURIComponent(withoutQuery);
    return decoded.split('/').pop() || url.slice(0, 40);
  }
};
